import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FreshnessBadge } from "../components/StatusBadge";
import {
  BarChart3, Users, Network, Layers, CreditCard, ShieldAlert, ScrollText, Inbox, Plus, Check, X, GitPullRequest, Eye, Upload, Download, Send, Copy, KeyRound, CircleCheck, TriangleAlert, Mail,
} from "lucide-react";

function CredentialsPanel({ items, onClose }) {
  const copy = (text, label) => {
    if (!text || text === "undefined") {
      toast.error(`No ${label.toLowerCase()} available to copy`);
      return;
    }
    const onSuccess = () => toast.success(`${label} copied to clipboard`);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(onSuccess).catch(() => {
        fallbackCopy(text, onSuccess, label);
      });
    } else {
      fallbackCopy(text, onSuccess, label);
    }
  };

  const fallbackCopy = (text, onSuccess, label) => {
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.setAttribute("readonly", "");
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      if (onSuccess) onSuccess();
    } catch {
      toast.error(`Unable to copy ${label.toLowerCase()}`);
    }
  };

  if (!items || items.length === 0) return null;

  return (
    <div data-testid="creds-panel" className="rounded-xl border border-[#1B6FA8]/30 bg-[#EAF3F9] p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-slate-900">Provider access credentials</h3>
        <button data-testid="creds-close" onClick={onClose} className="rounded p-1 text-slate-500 hover:bg-white"><X size={18} /></button>
      </div>
      <p className="mb-4 text-sm text-slate-600">
        Shown once — copy and share via your usual channel. Each provider can use this temporary password or set their own via the direct link. Onboarding does not depend on the email arriving.
      </p>
      <div className="space-y-3">
        {items.map((c, idx) => {
          const rowId = c.id || idx;
          const tempPw = c.temp_password || "";
          const resetLink = c.link || "";

          return (
            <div key={rowId} data-testid={`creds-row-${rowId}`} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="font-semibold text-slate-900">{c.name || "Provider"} <span className="font-normal text-slate-500">· {c.email}</span></div>
                {c.email_sent
                  ? <span data-testid={`creds-email-status-${rowId}`} className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"><CircleCheck size={13} /> Email sent</span>
                  : <span data-testid={`creds-email-status-${rowId}`} className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700"><TriangleAlert size={13} /> Email not sent — share password/link below</span>}
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Temp password</div>
                    <div data-testid={`creds-pw-${rowId}`} className="truncate font-mono text-sm font-bold text-slate-800">
                      {tempPw || "—"}
                    </div>
                  </div>
                  <button
                    data-testid={`copy-pw-${rowId}`}
                    onClick={() => copy(tempPw, "Password")}
                    disabled={!tempPw}
                    className="flex shrink-0 items-center gap-1 rounded border border-[#1B6FA8]/40 px-2.5 py-1.5 text-xs font-semibold text-teal-700 hover:bg-[#EAF3F9] active:scale-95 disabled:opacity-40"
                  >
                    <Copy size={13} /> Copy
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Set-password link</div>
                    <div data-testid={`creds-link-${rowId}`} className="truncate text-sm text-slate-600">
                      {resetLink || "—"}
                    </div>
                  </div>
                  <button
                    data-testid={`copy-link-${rowId}`}
                    onClick={() => copy(resetLink, "Link")}
                    disabled={!resetLink}
                    className="flex shrink-0 items-center gap-1 rounded border border-[#1B6FA8]/40 px-2.5 py-1.5 text-xs font-semibold text-teal-700 hover:bg-[#EAF3F9] active:scale-95 disabled:opacity-40"
                  >
                    <Copy size={13} /> Copy
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-600";
const btnCls = "rounded-lg bg-teal-700 px-4 py-2 text-sm font-bold text-white hover:bg-teal-800 disabled:opacity-60";

function Section({ title, children, actions }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
        {actions}
      </div>
      {children}
    </div>
  );
}

/* ---------------- Overview ---------------- */
function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data));
  }, []);
  if (!s) return <div className="text-slate-500">Loading…</div>;
  const cards = [
    ["Participating programs", s.total_programs],
    ["Reporting within 25h", `${s.reporting_within_25h} (${s.reporting_pct}%)`],
    ["Programs with open beds", s.programs_with_beds],
    ["Total accounts", s.total_users],
  ];
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Operating snapshot</h2>
          <p className="text-sm text-slate-500">LATP does not rank providers or publish performance statistics.</p>
        </div>
        <Link to="/search?preview=1" data-testid="view-as-user" className="flex items-center gap-2 rounded-lg bg-[#64b6ac] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#64b6ac]/90"><Eye size={16} /> View the app as a referral user</Link>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="font-display text-3xl font-extrabold text-slate-900">{v}</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-widest text-slate-400">{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Accounts ---------------- */
function Accounts() {
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "referral_partner", org_ids: [], program_ids: [] });
  const [creds, setCreds] = useState([]);
  const [forceReset, setForceReset] = useState(false);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/organizations").then((r) => setOrgs(r.data));
    api.get("/admin/programs").then((r) => setPrograms(r.data));
  }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success(`Account created${data.temp_password ? `. Temp password: ${data.temp_password}` : ""}`, { duration: 12000 });
      if (data.temp_password) {
        setCreds([data]);
      }
      setForm({ email: "", name: "", role: "referral_partner", org_ids: [], program_ids: [] });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const testOutgoingEmail = async () => {
    try {
      toast.info("Dispatching test notification…");
      const { data } = await api.post("/admin/test-email", { to: form.email || undefined });
      if (data.ok) {
        toast.success(`Test email sent successfully via ${data.provider}! Check ${data.target}`);
      } else {
        toast.warning(`Notification provider: ${data.error}`, { duration: 9000 });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/users/${id}`, patch);
      toast.success("Account updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const inviteAll = async () => {
    try {
      const { data } = await api.post(`/admin/users/invite-pending?force_change=${forceReset}`);
      setCreds(data.results || []);
      if (data.total_targets === 0) { toast.info("No pending providers to invite."); return; }
      if ((data.results || []).length === 0 && data.skipped_count) {
        toast.info(`Skipped ${data.skipped_count} provider(s) invited in the last few minutes.`, { duration: 8000 });
        return;
      }
      let msg = `Credentials ready for ${data.results.length} provider(s). Email delivered to ${data.email_sent_count}.`;
      if (data.skipped_count) msg += ` Skipped ${data.skipped_count} invited recently.`;
      toast.success(msg, { duration: 8000 });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const invite = async (id) => {
    try {
      const { data } = await api.post(`/admin/users/${id}/invite?force_change=${forceReset}`);
      setCreds([data]);
      if (data.email_sent) toast.success("Invite email sent. Credentials shown below.");
      else toast.warning("Email not sent — share the password/link shown below.", { duration: 8000 });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const generatePassword = async (id) => {
    try {
      const { data } = await api.post(`/admin/users/${id}/generate-password?force_change=${forceReset}`);
      setCreds([data]);
      toast.success("Temporary password generated. Copy it below.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const isProvider = form.role === "provider_admin" || form.role === "provider_reporter";

  return (
    <div className="space-y-4">
      <CredentialsPanel items={creds} onClose={() => setCreds([])} />
      <Section title="Create account (LATP-approved only)">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <input data-testid="new-user-name" className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="new-user-email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <select data-testid="new-user-role" className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, org_ids: [], program_ids: [] })}>
            <option value="referral_partner">Referral partner</option>
            <option value="provider_admin">Provider admin</option>
            <option value="provider_reporter">Provider reporter</option>
            <option value="auditor">Auditor</option>
            <option value="latp_admin">LATP admin</option>
          </select>
          <button data-testid="create-user-btn" onClick={create} className={btnCls}>
            <span className="flex items-center justify-center gap-1"><Plus size={15} /> Create</span>
          </button>
        </div>
        {isProvider && (
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Organization</label>
              <select className={inputCls} value={form.org_ids[0] || ""} onChange={(e) => setForm({ ...form, org_ids: e.target.value ? [e.target.value] : [] })}>
                <option value="">Select organization</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            {form.role === "provider_reporter" && (
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Assign programs (Ctrl/Cmd-click)</label>
                <select multiple className={`${inputCls} h-24`} value={form.program_ids}
                  onChange={(e) => setForm({ ...form, program_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}>
                  {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
          </div>
        )}
      </Section>

      <Section title={`Accounts (${users.length})`} actions={
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
            <input type="checkbox" data-testid="force-reset-checkbox" checked={forceReset} onChange={(e) => setForceReset(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-teal-700 focus:ring-teal-600" />
            Require password change at first sign-in
          </label>
          <button data-testid="test-email-btn" onClick={testOutgoingEmail} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <Mail size={15} /> Test email
          </button>
          <button data-testid="invite-pending-btn" onClick={inviteAll} className="flex items-center gap-2 rounded-lg bg-teal-700 px-3 py-2 text-sm font-bold text-white hover:bg-teal-800">
            <Send size={15} /> Invite all pending providers
          </button>
        </div>
      }>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-widest text-slate-400">
                <th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Role</th><th className="p-2">Status</th><th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-100">
                  <td className="p-2 font-semibold">{u.name}</td>
                  <td className="p-2 text-slate-600">{u.email}</td>
                  <td className="p-2 capitalize">{u.role.replace(/_/g, " ")}</td>
                  <td className="p-2">
                    <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${u.status === "approved" ? "bg-emerald-50 text-emerald-700" : u.status === "suspended" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{u.status}</span>
                  </td>
                  <td className="p-2">
                    <div className="flex flex-wrap gap-1">
                      <button data-testid={`invite-${u.id}`} onClick={() => invite(u.id)} className="rounded border border-teal-200 px-2 py-1 text-xs font-semibold text-teal-700 hover:bg-teal-50">Invite</button>
                      <button data-testid={`generate-pw-${u.id}`} onClick={() => generatePassword(u.id)} className="flex items-center gap-1 rounded border border-[#F07E1E]/40 px-2 py-1 text-xs font-semibold text-[#C25E0E] hover:bg-[#FDF2E7]"><KeyRound size={12} /> Password</button>
                      {u.status !== "approved" && <button data-testid={`approve-${u.id}`} onClick={() => update(u.id, { status: "approved" })} className="rounded border border-emerald-200 px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">Approve</button>}
                      {u.status !== "suspended" && <button data-testid={`suspend-${u.id}`} onClick={() => update(u.id, { status: "suspended" })} className="rounded border border-amber-200 px-2 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50">Suspend</button>}
                      {u.status !== "deactivated" && <button data-testid={`deactivate-${u.id}`} onClick={() => update(u.id, { status: "deactivated" })} className="rounded border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50">Deactivate</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

/* ---------------- Structure ---------------- */
function ManageFacilities({ orgs }) {
  const [facilities, setFacilities] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const load = () => {
    api.get("/facilities").then((r) => setFacilities(r.data));
    api.get("/admin/programs").then((r) => setPrograms(r.data));
  };
  useEffect(() => { load(); }, []);
  const deleteFacility = async (f) => {
    if (!window.confirm(`Delete facility "${f.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/facilities/${f.id}`); toast.success("Facility deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeProgram = async (p) => {
    if (!window.confirm(`Remove program "${p.name}"? It leaves the directory and reporting; capacity history is kept in the audit log.`)) return;
    try { await api.delete(`/admin/programs/${p.id}`); toast.success("Program removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (f) => {
    setEditing(f.id);
    setForm({ name: f.name || "", address: f.address || "", parish: f.parish || "", region: f.region || "",
              phone: f.phone || "", contact_name: f.contact_name || "", contact_email: f.contact_email || "",
              lat: f.lat ?? "", lng: f.lng ?? "", photo_url: f.photo_url || "" });
  };
  const save = async () => {
    try {
      const payload = { ...form, lat: form.lat === "" ? null : parseFloat(form.lat), lng: form.lng === "" ? null : parseFloat(form.lng) };
      await api.patch(`/admin/facilities/${editing}`, payload);
      toast.success("Facility updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, photo_url: data.photo_url }));
      toast.success("Photo uploaded — remember to Save changes");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const orgName = (id) => orgs.find((o) => o.id === id)?.name || "";
  return (
    <Section title={`Manage facilities (${facilities.length})`}>
      <div className="max-h-[60vh] space-y-2 overflow-auto">
        {facilities.map((f) => (
          <div key={f.id} data-testid={`facility-row-${f.id}`} className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                {f.photo_url
                  ? <img src={f.photo_url} alt="" className="h-12 w-16 rounded border border-slate-200 object-cover" />
                  : <div className="grid h-12 w-16 place-items-center rounded bg-slate-100 text-[10px] text-slate-400">No photo</div>}
                <div>
                  <div className="font-semibold text-slate-900">{f.name}</div>
                  <div className="text-xs text-slate-500">{orgName(f.org_id)} · {f.parish || "—"} · {f.address || "no address"}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`edit-facility-${f.id}`} onClick={() => (editing === f.id ? setEditing(null) : startEdit(f))} className="rounded border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                  {editing === f.id ? "Close" : "Edit"}
                </button>
                <button data-testid={`delete-facility-${f.id}`} onClick={() => deleteFacility(f)} className="rounded border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50">Delete</button>
              </div>
            </div>
            {programs.filter((p) => p.facility_id === f.id).length > 0 && (
              <div className="mt-2 space-y-1 border-t border-slate-100 pt-2">
                {programs.filter((p) => p.facility_id === f.id).map((p) => (
                  <div key={p.id} data-testid={`program-row-${p.id}`} className="flex items-center justify-between rounded bg-slate-50 px-2 py-1 text-xs">
                    <span className="text-slate-700">{p.name} <span className="text-slate-400">· ASAM {p.asam_level_code} · {p.population}</span></span>
                    <button data-testid={`remove-program-${p.id}`} onClick={() => removeProgram(p)} className="rounded border border-rose-200 px-2 py-0.5 font-semibold text-rose-700 hover:bg-rose-50">Remove</button>
                  </div>
                ))}
              </div>
            )}
            {editing === f.id && (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <input className={inputCls} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input data-testid={`edit-address-${f.id}`} className={inputCls} placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                  <input className={inputCls} placeholder="Parish" value={form.parish} onChange={(e) => setForm({ ...form, parish: e.target.value })} />
                  <input className={inputCls} placeholder="Region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
                  <input className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  <input data-testid={`edit-contact-${f.id}`} className={inputCls} placeholder="Contact name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                  <input className={inputCls} placeholder="Contact email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                  <input className={inputCls} placeholder="Latitude" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} />
                  <input className={inputCls} placeholder="Longitude" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <label className="text-sm text-slate-600">
                    <span className="cursor-pointer rounded-lg border border-slate-200 px-3 py-2 font-semibold hover:bg-slate-50">{uploading ? "Uploading…" : "Upload photo"}</span>
                    <input data-testid={`upload-photo-${f.id}`} type="file" accept="image/*" className="hidden" onChange={onUpload} />
                  </label>
                  {form.photo_url && <img src={form.photo_url} alt="" className="h-12 w-16 rounded border border-slate-200 object-cover" />}
                  <div className="ml-auto flex gap-2">
                    <button onClick={() => setEditing(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
                    <button data-testid={`save-facility-${f.id}`} onClick={save} className={btnCls}>Save changes</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function BulkPrograms() {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    const res = await api.get("/admin/programs/bulk-template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "latp_program_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/programs/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSummary(data);
      toast.success(`Created ${data.created} programs (${data.skipped} skipped, ${data.errors.length} errors)`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <Section title="Bulk add programs & bed pools">
      <p className="mb-3 text-xs text-slate-500">Download the template (one row per facility without a program, with matched intake phone, parish, and license pre-filled). Fill in ASAM level, program name, population, sex, and licensed beds, then upload — this creates the programs and bed pools so those facilities appear in referral search and on the map.</p>
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="download-template-btn" onClick={download} className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          <Download size={15} /> Download template
        </button>
        <label className="text-sm">
          <span className="flex cursor-pointer items-center gap-2 rounded-lg bg-teal-700 px-4 py-2 font-bold text-white hover:bg-teal-800">{busy ? "Uploading…" : (<><Upload size={15} /> Upload filled CSV</>)}</span>
          <input data-testid="upload-programs-input" type="file" accept=".csv" className="hidden" onChange={upload} />
        </label>
      </div>
      {summary && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
          <div className="font-semibold">Created {summary.created} · Skipped {summary.skipped} · Errors {summary.errors.length}</div>
          {summary.errors.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-rose-700">{summary.errors.slice(0, 40).map((er, i) => <li key={i}>{er}</li>)}</ul>}
          {summary.notes && summary.notes.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-teal-700">{summary.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
        </div>
      )}
    </Section>
  );
}

function ManageOrganizations({ orgs, facilities, onReload }) {
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", contact_email: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const startEdit = (o) => {
    setEditing(o.id);
    setForm({ name: o.name || "", contact_email: o.contact_email || "", phone: o.phone || "" });
  };

  const saveEdit = async () => {
    if (!form.name.trim()) {
      toast.error("Organization name is required");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/organizations/${editing}`, form);
      toast.success("Organization updated");
      setEditing(null);
      if (onReload) onReload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteOrg = async (o) => {
    const facCount = facilities.filter((f) => f.org_id === o.id).length;
    const warning =
      facCount > 0
        ? `Delete organization "${o.name}"? Note: ${facCount} facility(s) are currently attached to this organization. This cannot be undone.`
        : `Delete organization "${o.name}"? This cannot be undone.`;
    if (!window.confirm(warning)) return;
    try {
      await api.delete(`/admin/organizations/${o.id}`);
      toast.success("Organization deleted");
      if (onReload) onReload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filtered = orgs.filter((o) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (o.name || "").toLowerCase().includes(s) ||
      (o.contact_email || "").toLowerCase().includes(s) ||
      (o.phone || "").toLowerCase().includes(s)
    );
  });

  return (
    <Section title={`Manage organizations (${orgs.length})`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <input
          data-testid="org-search"
          placeholder="Filter organizations by name, email, or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-sm rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-teal-600 sm:text-sm"
        />
        <div className="text-xs text-slate-500">
          Showing {filtered.length} of {orgs.length} organizations
        </div>
      </div>

      <div className="max-h-[50vh] space-y-2 overflow-auto">
        {filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">
            {search ? `No organizations found matching "${search}".` : "No organizations created yet."}
          </div>
        ) : (
          filtered.map((o) => {
            const facCount = facilities.filter((f) => f.org_id === o.id).length;
            const isEditing = editing === o.id;

            return (
              <div key={o.id} data-testid={`org-row-${o.id}`} className="rounded-lg border border-slate-200 p-3 hover:border-slate-300">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{o.name}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                        {facCount} {facCount === 1 ? "facility" : "facilities"}
                      </span>
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {o.contact_email ? <span>{o.contact_email}</span> : <span className="text-slate-400">No email</span>}
                      {o.phone ? <span> · {o.phone}</span> : ""}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`edit-org-${o.id}`}
                      onClick={() => (isEditing ? setEditing(null) : startEdit(o))}
                      className="rounded border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      {isEditing ? "Close" : "Edit"}
                    </button>
                    <button
                      data-testid={`delete-org-${o.id}`}
                      onClick={() => deleteOrg(o)}
                      className="rounded border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {isEditing && (
                  <div className="mt-3 border-t border-slate-100 pt-3">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold text-slate-500">Name</label>
                        <input
                          className={inputCls}
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Organization name"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold text-slate-500">Contact email</label>
                        <input
                          className={inputCls}
                          value={form.contact_email}
                          onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                          placeholder="Email"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold text-slate-500">Phone</label>
                        <input
                          className={inputCls}
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="Phone"
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        onClick={() => setEditing(null)}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600"
                      >
                        Cancel
                      </button>
                      <button
                        data-testid={`save-org-${o.id}`}
                        onClick={saveEdit}
                        disabled={busy}
                        className={btnCls}
                      >
                        {busy ? "Saving…" : "Save changes"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </Section>
  );
}

function Structure() {
  const [orgs, setOrgs] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [units, setUnits] = useState([]);
  const [asam, setAsam] = useState([]);
  const [payers, setPayers] = useState([]);

  const [org, setOrg] = useState({ name: "", contact_email: "", phone: "" });
  const [fac, setFac] = useState({ org_id: "", name: "", address: "", parish: "", region: "", lat: "", lng: "", phone: "" });
  const [prog, setProg] = useState({ org_id: "", facility_id: "", name: "", asam_level_code: "", population: "adult", sex_designation: "coed", intake_phone: "", after_hours_phone: "", admission_hours: "", moud: false, cooccurring: false, pregnancy_parenting: false });
  const [unit, setUnit] = useState({ program_id: "", name: "", licensed_beds: 0 });

  const loadAll = () => {
    api.get("/organizations").then((r) => setOrgs(r.data));
    api.get("/facilities").then((r) => setFacilities(r.data));
    api.get("/admin/programs").then((r) => setPrograms(r.data));
    api.get("/reporting-units").then((r) => setUnits(r.data));
  };
  useEffect(() => {
    loadAll();
    api.get("/asam-levels").then((r) => setAsam(r.data.filter((l) => l.status === "active")));
    api.get("/payers").then((r) => setPayers(r.data));
  }, []);

  const call = async (fn, label) => {
    try { await fn(); toast.success(`${label} created`); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleAddOrg = () => {
    if (!org.name || !org.name.trim()) {
      toast.error("Please enter an organization name");
      return;
    }
    call(() => api.post("/admin/organizations", org).then(() => setOrg({ name: "", contact_email: "", phone: "" })), "Organization");
  };

  return (
    <div className="space-y-4">
      <Section title="Organization">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <input data-testid="org-name" className={inputCls} placeholder="Name" value={org.name} onChange={(e) => setOrg({ ...org, name: e.target.value })} />
          <input className={inputCls} placeholder="Contact email" value={org.contact_email} onChange={(e) => setOrg({ ...org, contact_email: e.target.value })} />
          <input className={inputCls} placeholder="Phone" value={org.phone} onChange={(e) => setOrg({ ...org, phone: e.target.value })} />
          <button data-testid="create-org-btn" onClick={handleAddOrg} className={btnCls}>Add organization</button>
        </div>
      </Section>

      <ManageOrganizations orgs={orgs} facilities={facilities} onReload={loadAll} />

      <Section title="Facility / campus">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <select data-testid="fac-org" className={inputCls} value={fac.org_id} onChange={(e) => setFac({ ...fac, org_id: e.target.value })}>
            <option value="">Organization…</option>{orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <input data-testid="fac-name" className={inputCls} placeholder="Facility name" value={fac.name} onChange={(e) => setFac({ ...fac, name: e.target.value })} />
          <input className={inputCls} placeholder="Address" value={fac.address} onChange={(e) => setFac({ ...fac, address: e.target.value })} />
          <input data-testid="fac-parish" className={inputCls} placeholder="Parish" value={fac.parish} onChange={(e) => setFac({ ...fac, parish: e.target.value })} />
          <input className={inputCls} placeholder="Region" value={fac.region} onChange={(e) => setFac({ ...fac, region: e.target.value })} />
          <input className={inputCls} placeholder="Latitude" value={fac.lat} onChange={(e) => setFac({ ...fac, lat: e.target.value })} />
          <input className={inputCls} placeholder="Longitude" value={fac.lng} onChange={(e) => setFac({ ...fac, lng: e.target.value })} />
          <button data-testid="create-fac-btn" onClick={() => call(() => api.post("/admin/facilities", { ...fac, lat: fac.lat ? parseFloat(fac.lat) : null, lng: fac.lng ? parseFloat(fac.lng) : null }).then(() => setFac({ ...fac, name: "", address: "", lat: "", lng: "" })), "Facility")} className={btnCls}>Add facility</button>
        </div>
      </Section>

      <ManageFacilities orgs={orgs} />

      <BulkPrograms />

      <Section title="Licensed program">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <select data-testid="prog-org" className={inputCls} value={prog.org_id} onChange={(e) => setProg({ ...prog, org_id: e.target.value, facility_id: "" })}>
            <option value="">Organization…</option>{orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <select data-testid="prog-fac" className={inputCls} value={prog.facility_id} onChange={(e) => setProg({ ...prog, facility_id: e.target.value })}>
            <option value="">Facility…</option>{facilities.filter((f) => f.org_id === prog.org_id).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <input data-testid="prog-name" className={inputCls} placeholder="LA program name" value={prog.name} onChange={(e) => setProg({ ...prog, name: e.target.value })} />
          <select data-testid="prog-asam" className={inputCls} value={prog.asam_level_code} onChange={(e) => setProg({ ...prog, asam_level_code: e.target.value })}>
            <option value="">ASAM level…</option>{asam.map((l) => <option key={l.code} value={l.code}>{l.code} — {l.official_name}</option>)}
          </select>
          <select className={inputCls} value={prog.population} onChange={(e) => setProg({ ...prog, population: e.target.value })}>
            <option value="adult">Adult</option><option value="adolescent">Adolescent</option>
          </select>
          <select className={inputCls} value={prog.sex_designation} onChange={(e) => setProg({ ...prog, sex_designation: e.target.value })}>
            <option value="coed">Co-ed</option><option value="men">Men</option><option value="women">Women</option><option value="flex">Flexible</option>
          </select>
          <input className={inputCls} placeholder="Intake phone" value={prog.intake_phone} onChange={(e) => setProg({ ...prog, intake_phone: e.target.value })} />
          <input className={inputCls} placeholder="Admission hours" value={prog.admission_hours} onChange={(e) => setProg({ ...prog, admission_hours: e.target.value })} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {["moud", "cooccurring", "pregnancy_parenting"].map((k) => (
            <label key={k} className="flex items-center gap-1.5 text-sm capitalize text-slate-600">
              <input type="checkbox" checked={prog[k]} onChange={(e) => setProg({ ...prog, [k]: e.target.checked })} /> {k.replace(/_/g, " ")}
            </label>
          ))}
          <button data-testid="create-prog-btn" onClick={() => call(() => api.post("/admin/programs", prog).then(() => setProg({ ...prog, name: "", asam_level_code: "" })), "Program")} className={`${btnCls} ml-auto`}>Add program</button>
        </div>
      </Section>

      <Section title="Reporting unit / bed pool">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <select data-testid="unit-prog" className={inputCls} value={unit.program_id} onChange={(e) => setUnit({ ...unit, program_id: e.target.value })}>
            <option value="">Program…</option>{programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input data-testid="unit-name" className={inputCls} placeholder="Unit name" value={unit.name} onChange={(e) => setUnit({ ...unit, name: e.target.value })} />
          <input data-testid="unit-beds" type="number" className={inputCls} placeholder="Licensed beds" value={unit.licensed_beds} onChange={(e) => setUnit({ ...unit, licensed_beds: parseInt(e.target.value || "0", 10) })} />
          <button data-testid="create-unit-btn" onClick={() => call(() => api.post("/admin/reporting-units", unit).then(() => setUnit({ program_id: "", name: "", licensed_beds: 0 })), "Reporting unit")} className={btnCls}>Add unit</button>
        </div>
        <div className="mt-4 text-xs text-slate-500">
          {orgs.length} organizations · {facilities.length} facilities · {programs.length} programs · {units.length} reporting units
        </div>
      </Section>
    </div>
  );
}

/* ---------------- ASAM ---------------- */
function AsamLevels() {
  const [levels, setLevels] = useState([]);
  const [form, setForm] = useState({ code: "", official_name: "", population: "adult", status: "active" });
  const load = () => api.get("/asam-levels").then((r) => setLevels(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (code, level, status) => {
    await api.patch(`/admin/asam-levels/${code}`, { ...level, status });
    toast.success(`${code} → ${status}`);
    load();
  };
  return (
    <Section title="Levels of care (ASAM)" actions={
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="asam-code" className={`${inputCls} w-24`} placeholder="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
        <input data-testid="asam-name" className={`${inputCls} w-56`} placeholder="Official LA name" value={form.official_name} onChange={(e) => setForm({ ...form, official_name: e.target.value })} />
        <button data-testid="create-asam-btn" onClick={async () => { try { await api.post("/admin/asam-levels", form); toast.success("Level added"); setForm({ code: "", official_name: "", population: "adult", status: "active" }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} className={btnCls}>Add level</button>
      </div>
    }>
      <p className="mb-3 text-xs text-slate-500">3.2-WM and 3.3 are marked legacy until LDH confirms the level list. New capacity reports cannot be filed against an inactive level.</p>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs font-bold uppercase tracking-widest text-slate-400"><th className="p-2">Code</th><th className="p-2">Official name</th><th className="p-2">Population</th><th className="p-2">Status</th><th className="p-2">Set</th></tr></thead>
        <tbody>
          {levels.map((l) => (
            <tr key={l.code} className="border-t border-slate-100">
              <td className="p-2 font-bold">{l.code}</td>
              <td className="p-2 text-slate-600">{l.official_name}</td>
              <td className="p-2 capitalize">{l.population}</td>
              <td className="p-2"><span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${l.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{l.status}</span></td>
              <td className="p-2">
                <div className="flex gap-1">
                  <button data-testid={`asam-active-${l.code}`} onClick={() => setStatus(l.code, l, "active")} className="rounded border border-emerald-200 px-2 py-1 text-xs font-semibold text-emerald-700">Active</button>
                  <button data-testid={`asam-legacy-${l.code}`} onClick={() => setStatus(l.code, l, "legacy")} className="rounded border border-amber-200 px-2 py-1 text-xs font-semibold text-amber-700">Legacy</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

/* ---------------- Payers ---------------- */
function Payers() {
  const [payers, setPayers] = useState([]);
  const [form, setForm] = useState({ name: "", category: "commercial" });
  const load = () => api.get("/payers").then((r) => setPayers(r.data));
  useEffect(() => { load(); }, []);
  return (
    <Section title="Insurance & payer reference list" actions={
      <div className="flex items-center gap-2">
        <input data-testid="payer-name" className={`${inputCls} w-48`} placeholder="Payer name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
          {["medicaid_mco", "medicare", "commercial", "tricare", "self_pay", "indigent", "scholarship"].map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
        </select>
        <button data-testid="create-payer-btn" onClick={async () => { await api.post("/admin/payers", form); toast.success("Payer added"); setForm({ name: "", category: "commercial" }); load(); }} className={btnCls}>Add</button>
      </div>
    }>
      <div className="flex flex-wrap gap-2">
        {payers.map((p) => (
          <span key={p.name} className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
            {p.name} <span className="text-slate-400">· {p.category.replace(/_/g, " ")}</span>
          </span>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- Compliance ---------------- */
function Compliance() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/compliance").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const toggleRemoval = async (id, val) => {
    await api.patch(`/admin/programs/${id}`, { compliance_removed: val });
    toast.success(val ? "Removed until reporting resumes" : "Restored to results");
    load();
  };
  return (
    <Section title="Reporting freshness & compliance">
      <p className="mb-3 text-xs text-slate-500">Stale (25h) is automatic and keeps the program visible with a warning. Administrative removal is a separate compliance action.</p>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs font-bold uppercase tracking-widest text-slate-400"><th className="p-2">Program</th><th className="p-2">Facility</th><th className="p-2">Last update</th><th className="p-2">Status</th>{user?.role === "latp_admin" && <th className="p-2">Action</th>}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              <td className="p-2 font-semibold">{r.name}</td>
              <td className="p-2 text-slate-600">{r.facility_name}</td>
              <td className="p-2 text-slate-500">{r.last_updated ? new Date(r.last_updated).toLocaleString() : "never"}</td>
              <td className="p-2"><div className="flex items-center gap-2"><FreshnessBadge status={r.freshness} />{r.compliance_removed && <span className="rounded-md bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">removed</span>}</div></td>
              {user?.role === "latp_admin" && (
                <td className="p-2">
                  {r.compliance_removed
                    ? <button data-testid={`restore-${r.id}`} onClick={() => toggleRemoval(r.id, false)} className="rounded border border-emerald-200 px-2 py-1 text-xs font-semibold text-emerald-700">Restore</button>
                    : <button data-testid={`remove-${r.id}`} onClick={() => toggleRemoval(r.id, true)} className="rounded border border-rose-200 px-2 py-1 text-xs font-semibold text-rose-700">Remove</button>}
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && <tr><td className="p-4 text-slate-500" colSpan={5}>No active programs.</td></tr>}
        </tbody>
      </table>
    </Section>
  );
}

/* ---------------- Requests ---------------- */
function Requests() {
  const [reqs, setReqs] = useState([]);
  const [creds, setCreds] = useState([]);
  const load = () => api.get("/admin/account-requests").then((r) => setReqs(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (id) => {
    try {
      const { data } = await api.post(`/admin/account-requests/${id}/approve`);
      if (data.temp_password) {
        setCreds([data]);
      }
      toast.success(`Approved. Temp password: ${data.temp_password}`, { duration: 12000 });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <div className="space-y-4">
      <CredentialsPanel items={creds} onClose={() => setCreds([])} />
      <Section title="Reporting-contact account requests">
        {reqs.length === 0 && <div className="text-sm text-slate-500">No pending requests.</div>}
        <div className="space-y-2">
          {reqs.map((r) => (
            <div key={r.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div><div className="font-semibold">{r.name} <span className="text-slate-400">· {r.email}</span></div><div className="text-xs text-slate-500 capitalize">{r.role.replace(/_/g, " ")} · {r.status}</div></div>
              {r.status === "pending" && <button data-testid={`approve-req-${r.id}`} onClick={() => approve(r.id)} className="rounded-lg bg-teal-700 px-3 py-1.5 text-sm font-semibold text-white">Approve</button>}
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

/* ---------------- Audit ---------------- */
function Changes() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/change-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try { await api.post(`/admin/change-requests/${id}/${action}`); toast.success(`Change ${action}d`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Section title="Provider change requests">
      <p className="mb-3 text-xs text-slate-500">Proposed edits to facility info, insurance, and transportation are applied only after LATP approval.</p>
      {rows.length === 0 && <div className="text-sm text-slate-500">No change requests.</div>}
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} data-testid={`change-req-${r.id}`} className="rounded-lg border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">{r.program_name}</div>
                <div className="text-xs text-slate-500">Proposed by {r.requested_by_email} · {new Date(r.created_at).toLocaleString()} · <span className={`font-semibold capitalize ${r.status === "pending" ? "text-amber-700" : r.status === "approved" ? "text-emerald-700" : "text-rose-700"}`}>{r.status}</span></div>
              </div>
              {r.status === "pending" && (
                <div className="flex gap-2">
                  <button data-testid={`approve-change-${r.id}`} onClick={() => act(r.id, "approve")} className="rounded border border-emerald-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">Approve</button>
                  <button data-testid={`reject-change-${r.id}`} onClick={() => act(r.id, "reject")} className="rounded border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50">Reject</button>
                </div>
              )}
            </div>
            {r.note && <div className="mt-2 text-sm text-slate-600">Note: {r.note}</div>}
            <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
              {Object.entries(r.changes).map(([k, v]) => (
                <div key={k} className="rounded bg-slate-50 px-2 py-1 text-xs"><span className="font-semibold text-slate-700">{k}:</span> <span className="text-slate-600">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- Audit ---------------- */
function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)); }, []);
  return (
    <Section title="Audit log (append-only)">
      <div className="max-h-[60vh] overflow-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs font-bold uppercase tracking-widest text-slate-400"><th className="p-2">When</th><th className="p-2">Actor</th><th className="p-2">Action</th><th className="p-2">Entity</th></tr></thead>
          <tbody>
            {logs.map((l, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="p-2 text-slate-500">{new Date(l.at).toLocaleString()}</td>
                <td className="p-2">{l.actor_email || "system"}</td>
                <td className="p-2 font-semibold text-teal-800">{l.action}</td>
                <td className="p-2 text-slate-500">{l.entity} {l.entity_id ? `#${String(l.entity_id).slice(0, 8)}` : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "latp_admin";
  const TABS = isAdmin
    ? [
        ["overview", "Overview", BarChart3, Overview],
        ["accounts", "Accounts", Users, Accounts],
        ["requests", "Requests", Inbox, Requests],
        ["changes", "Changes", GitPullRequest, Changes],
        ["structure", "Structure", Network, Structure],
        ["asam", "ASAM levels", Layers, AsamLevels],
        ["payers", "Payers", CreditCard, Payers],
        ["compliance", "Compliance", ShieldAlert, Compliance],
        ["audit", "Audit", ScrollText, Audit],
      ]
    : [
        ["overview", "Overview", BarChart3, Overview],
        ["compliance", "Compliance", ShieldAlert, Compliance],
        ["audit", "Audit", ScrollText, Audit],
      ];
  const [tab, setTab] = useState(TABS[0][0]);
  const Active = TABS.find((t) => t[0] === tab)[3];

  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">
        {isAdmin ? "LATP administration" : "Oversight"}
      </h1>
      <div className="mb-5 mt-3 flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map(([id, label, Icon]) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors ${
              tab === id ? "border-teal-700 text-teal-800" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>
      <Active />
    </div>
  );
}
